import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import {
  Grid,
  Paper,
  Toolbar,
  Typography,
  Divider,
  Box,
  useMediaQuery,
  Tooltip,
  IconButton,
  Dialog,
  DialogContent,
  Stack,
  Button,
  TextField,
  FormControlLabel,
  Checkbox,
  Chip,
  DialogActions,
} from "@mui/material";
import Sourceslist from "./Sourceslist";
import { Close as CloseIcon } from "@mui/icons-material";
import Sourcesjobslist from "./Sourcesjobslist";
import FilterListIcon from "@mui/icons-material/FilterList";
import SourcesJobdetailsview from "./SourcesJobdetailsvew";
import Sourcescontacts from "./Sourcescontacts";
import TrendingFlatIcon from "@mui/icons-material/TrendingFlat";
import { useDispatch, useSelector } from "react-redux";
import { getSolrJobsAction, getSourcesAction, selectedJobAction, getJobDetailsAction } from "../redux/action";
import generateSourceJobsQuery from "../../helpers/solrSourceJobs";
import { appSelector } from "../redux/selector";
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

const Sources = () => {
  const dispatch = useDispatch();
  const { selectedSource, sourcesList, selectedJob, sourcesLoading } = useSelector(appSelector);

  // --- state
  const [isSearching, setIsSearching] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterDialogOpen, setFilterDialogOpen] = useState(false);
  const [days, setDays] = useState(7);

  // filters
  const [selectedJobTypes, setSelectedJobTypes] = useState([]);
  const [companySearch, setCompanySearch] = useState("");
  const [jobTitles, setJobTitles] = useState([]);
  const [jobTitleInput, setJobTitleInput] = useState("");
  const [jobTitleError, setJobTitleError] = useState("");
  const [isFilterApplied, setIsFilterApplied] = useState(false);
  /**
   * 
   * 
   * const [appliedFilters, setAppliedFiltersState] = useState({
    company: "",
    job_titles: [],
    job_types: [],
  });
   */

  // jobs
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [limit] = useState(200);
  const [hasMore, setHasMore] = useState(true);

  // sources loading state
  const [loadingSources, setLoadingSources] = useState(false);

  // Cache for API calls to prevent redundant requests
  const jobsCache = useRef(new Map());
  const lastFetchParams = useRef(null);

  // Refs to store current filter values to avoid closure issues
  const companySearchRef = useRef(companySearch);
  const jobTitlesRef = useRef(jobTitles);
  const selectedJobTypesRef = useRef(selectedJobTypes);

  const fetchJobs = useCallback(async (
    resetPage = 0,
    filters = null,
    append = false
  ) => {
    const currentFilters = filters || {
      company: companySearchRef.current,
      job_titles: jobTitlesRef.current,
      job_types: selectedJobTypesRef.current,
    };

    if (!selectedSource?.source_name) {
      return;
    }

    const cacheKey = JSON.stringify({
      sourceName: selectedSource.source_name,
      resetPage,
      filters: currentFilters,
      limit
    });
    if (!append && resetPage === 0 && jobsCache.current.has(cacheKey)) {
      const cachedData = jobsCache.current.get(cacheKey);
      //console.log('Using cached data for:', selectedSource.source_name);
      setJobs(cachedData.jobs);
      setHasMore(cachedData.hasMore);
      setLoading(false);
      if (cachedData.jobs?.length > 0) {
        //console.log("cachedData.jobs[0]", cachedData.jobs[0])
        dispatch(selectedJobAction(cachedData.jobs[0]));
      }
      return;
    }

    //console.log('Fetching fresh data for:', selectedSource.source_name);
    setLoading(true);

    const solrURL = generateSourceJobsQuery(
      { sourcename: `"${selectedSource.source_name}"` },
      resetPage,
      limit,
      { ...currentFilters },
    );

    try {
      const data = await dispatch(getSolrJobsAction(solrURL));
      //console.log('Received data:', data?.length, 'jobs');

      if (append) {
        setJobs((prev) => [...prev, data]);
      } else {
        const jobsArray = data?.jobs || data || [];
        setJobs(jobsArray);

        // Cache the response (only for first page requests)
        if (resetPage === 0) {
          const hasMoreData = jobsArray.length >= limit;
          jobsCache.current.set(cacheKey, {
            jobs: jobsArray,
            hasMore: hasMoreData
          });
        }

        // Select first job automatically
        if (jobsArray.length > 0) {
          //console.log("jobsArray[0]", jobsArray[0])
          dispatch(selectedJobAction(jobsArray[0]));
        }
      }

      // Update hasMore based on returned data length
      const jobsLength = append ? data?.length : (data?.jobs || data || []).length;
      setHasMore(jobsLength >= limit);

    } catch (err) {
      console.error("Error fetching jobs:", err);
      setJobs([]);
      setHasMore(false);
    } finally {
      setLoading(false);
    }
  }, [selectedSource?.source_name, limit, dispatch]);

  useEffect(() => {
    setPage(0);
    setJobs([]);
    setHasMore(true);
  }, [selectedSource?.source_name]);

  useEffect(() => {
    if (selectedSource) {
      const filters = {
        company: companySearchRef.current,
        job_titles: jobTitlesRef.current,
        job_types: selectedJobTypesRef.current,
      };
      //fetchJobs(0, filters, false);
    }
  }, [selectedSource, fetchJobs]);

  // Keep refs updated with current state values
  useEffect(() => {
    companySearchRef.current = companySearch;
  }, [companySearch]);

  useEffect(() => {
    jobTitlesRef.current = jobTitles;
  }, [jobTitles]);

  useEffect(() => {
    selectedJobTypesRef.current = selectedJobTypes;
  }, [selectedJobTypes]);

  useEffect(() => {
    setCompanySearch("");
    setJobTitles([]);
    setSelectedJobTypes([]);
  }, [])

  // --- when source changes → fetch jobs (optimized with proper dependencies)
  useEffect(() => {
    if (selectedSource) {
      setPage(0);
      const filters = {
        company: companySearchRef.current,
        job_titles: jobTitlesRef.current,
        job_types: selectedJobTypesRef.current,
      };
      fetchJobs(0, filters, false);
    }
  }, [selectedSource]);

  // Memoized loadMoreJobs function for infinite scroll
  const loadMoreJobs = useCallback(async () => {
    const nextPage = page + limit;
    setPage(nextPage);
    const filters = {
      company: companySearchRef.current,
      job_titles: jobTitlesRef.current,
      job_types: selectedJobTypesRef.current,
    };
    //await fetchJobs(nextPage, filters, true);
  }, [page, limit, fetchJobs]);

  // Memoized filter dialog handlers
  const handleApplyFilters = useCallback(async () => {
    const filters = {
      company: companySearchRef.current,
      job_titles: jobTitlesRef.current,
      job_types: selectedJobTypesRef.current,
    };
    setFilterDialogOpen(false);
    
    const hasFilters = Boolean(
      (filters.company && filters.company.trim() !== "") ||
      filters.job_titles.length > 0 ||
      filters.job_types.length > 0
    );
    setIsFilterApplied(hasFilters);
    
    setPage(0);
    await fetchJobs(0, filters, false);
  }, [fetchJobs]);

  const handleClearAllFilters = useCallback(async () => {
    setCompanySearch("");
    setJobTitles([]);
    setSelectedJobTypes([]);
    setFilterDialogOpen(false);
    setIsFilterApplied(false);
    setPage(0);
    const filters = { company: "", job_titles: [], job_types: [] };
    await fetchJobs(0, filters, false);
  }, [fetchJobs]);

  // --- responsive helpers
  const isDesktopLarge = useMediaQuery("(min-width: 1920px)");
  const isDesktopMedium = useMediaQuery(
    "(min-width: 1440px) and (max-width: 1919px)"
  );
  const isDesktopSmall = useMediaQuery(
    "(min-width: 1200px) and (max-width: 1439px)"
  );

  const panelPaperStyles = useMemo(
    () => ({
      borderRadius: "8px",
      height: "100%",
      display: "flex",
      flexDirection: "column",
      overflow: "hidden",
    }),
    []
  );

  const panelScrollSx = useMemo(
    () => ({
      flex: 1,
      minHeight: 0,
      overflowY: "auto",
      overflowX: "hidden",
    }),
    []
  );

  // Memoized job type options
  const jobTypes = useMemo(() => [
    { id: 1, label: "Full-Time" },
    { id: 2, label: "Part-Time" },
    { id: 3, label: "Internship" },
    { id: 4, label: "W2 - Permanent" },
    { id: 5, label: "W2 - Contract" },
    { id: 6, label: "1099-Contract" },
    { id: 7, label: "Contract to Hire" },
    { id: 8, label: "Need H1B" },
    { id: 9, label: "Hourly Employee" },
    { id: 10, label: "Contract Based" },
    { id: 11, label: "Contract - Corp-to-Corp" },
    { id: 13, label: "Contract - Independent" },
    { id: 14, label: "Contract to Hire - Corp-to-Corp" },
    { id: 15, label: "Contract to Hire - W2" },
    { id: 16, label: "Contract to Hire - Independent" },
  ], []);


  const handleSearchTermInputChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleCompanySearchInputChange = (e) => {
    const value = e.target.value;
    setCompanySearch(value);
    //console.log('Company search input changed to:', value);
  };


  const handleJobTitleInputFieldChange = (e) => {
    const value = e.target.value;
    setJobTitleInput(value);
    setJobTitleError("");
    //console.log('Job title input changed to:', value);
  };

  const handleJobTitleKeyDown = (e) => {
    if (e.key === "Enter") {
      const trimmed = jobTitleInput.trim();
      if (!trimmed) return;
      if (jobTitles.includes(trimmed)) {
        setJobTitleError("This job title is already added.");
      } else {
        setJobTitles([...jobTitles, trimmed]);
        setJobTitleInput("");
        setJobTitleError("");
      }
    }
  };

  const handleAddJobTitle = () => {
    const trimmed = jobTitleInput.trim();
    if (!trimmed) return;
    if (jobTitles.includes(trimmed)) {
      setJobTitleError("This job title is already added.");
    } else {
      setJobTitles([...jobTitles, trimmed]);
      setJobTitleInput("");
      setJobTitleError("");
    }
  };

  const handleRemoveJobTitle = (titleToRemove) => {
    setJobTitles((prev) => prev.filter((t) => t !== titleToRemove));
  };

  const handleJobTypeToggle = (jobTypeId) => {
    setSelectedJobTypes((prev) =>
      prev.includes(jobTypeId)
        ? prev.filter((id) => id !== jobTypeId)
        : [...prev, jobTypeId]
    );
  };

  // Stable callback to avoid re-renders in memoized children
  const handleSelectJob = useCallback((job) => {
    //console.log("selectedJob", job)
    dispatch(selectedJobAction(job));
  }, [dispatch]);

  // Add cache cleanup and debounce timer cleanup effect
  useEffect(() => {
    return () => {
      if (jobsCache.current.size > 20) {
        const entries = Array.from(jobsCache.current.entries());
        // Keep only the 10 most recent entries
        jobsCache.current.clear();
        entries.slice(-10).forEach(([key, value]) => {
          jobsCache.current.set(key, value);
        });
      }
    };
  }, []);

  return (
    <>
      <Box
        sx={{
          height: "calc(100vh - 66px)",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          //pt: 0.25,
          ml: { xs: -1, md: -2 },
          mr: { xs: -0.5, md: -1 },
          width: { xs: "calc(100% + 12px)", md: "calc(100% + 24px)" },
          boxSizing: "border-box",
        }}
      >
        <Grid container spacing={0.5} sx={{ flex: 1, minHeight: 0, m: 0, width: "100%" }}>
          {/* --- Sources list --- */}
          <Grid item xs={2.4} sx={{ height: "100%", minHeight: 0 }}>
            <Paper elevation={2} sx={panelPaperStyles}>
              <Toolbar
                sx={{
                  flexShrink: 0,
                  justifyContent: "space-between",
                  minHeight: "48px !important",
                  bgcolor: "#fff",
                  borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
                }}
              >
                {!isSearching ? (
                  <Typography variant="h6" sx={{ fontWeight: 500, fontSize: "16px", margin: 0 }} paragraph>
                    Sources
                  </Typography>
                ) : (
                  <>
                    <input
                      type="text"
                      value={searchTerm}
                      onChange={handleSearchTermInputChange}
                      placeholder="Search source..."
                      autoFocus
                      style={{
                        padding: "8px",
                        borderRadius: "4px",
                        border: "1px solid #ccc",
                      }}
                    />
                    <CloseIcon
                      onClick={() => {
                        setIsSearching(false);
                        setSearchTerm("");
                      }}
                      style={{ cursor: "pointer", color: "#797070", marginLeft: "8px" }}
                    />
                  </>
                )}
              </Toolbar>
              <Box
                sx={{
                  flex: 1,
                  minHeight: 0,
                  display: "flex",
                  flexDirection: "column",
                  overflow: "hidden",
                }}
              >
                <Sourceslist
                  sources={sourcesList}
                  loading={loadingSources}
                  searchTerm={searchTerm}
                />
              </Box>
            </Paper>
          </Grid>

          {/* --- Jobs list --- */}
          <Grid item xs={2.6} sx={{ height: "100%", minHeight: 0 }}>
            <Paper elevation={2} sx={panelPaperStyles}>
              <Toolbar
                sx={{
                  flexShrink: 0,
                  minHeight: "48px !important",
                  bgcolor: "#fff",
                  borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
                }}
              >
                <Box sx={{ flexGrow: 1, display: "flex", justifyContent: "space-between", alignItems: 'center' }}>
                  <Typography
                    sx={{
                      margin: 0,
                      fontWeight: 500,
                      fontSize: isDesktopLarge
                        ? "16px"
                        : isDesktopMedium
                          ? "16px"
                          : isDesktopSmall
                            ? "14px"
                            : "13px",
                    }}
                    paragraph
                  >
                    List Of Jobs
                  </Typography>
                  <Tooltip title="Filters">
                    <IconButton onClick={() => setFilterDialogOpen(true)}>
                      <FilterListIcon sx={{ color: "#696969", fontSize: "22px" }} />
                      {isFilterApplied && (
                        <Box
                          sx={{
                            position: "absolute",
                            top: 6,
                            right: 6,
                            width: 8,
                            height: 8,
                            bgcolor: "#1976d2",
                            borderRadius: "50%",
                          }}
                        />
                      )}
                    </IconButton>
                  </Tooltip>
                </Box>
              </Toolbar>
              <Box id="sources-jobs-scroll" sx={panelScrollSx}>
                <Sourcesjobslist
                  jobs={jobs}
                  loading={loading || sourcesLoading}
                  hasMore={hasMore}
                  onLoadMore={loadMoreJobs}
                  onSelectJob={handleSelectJob}
                  selectedJob={selectedJob}
                />
              </Box>
            </Paper>
          </Grid>

          {/* --- Job details --- */}
          <Grid item xs={4.6} sx={{ height: "100%", minHeight: 0 }}>
            <Paper elevation={2} sx={panelPaperStyles}>
              <Toolbar
                sx={{
                  flexShrink: 0,
                  minHeight: "48px !important",
                  bgcolor: "#fff",
                  borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
                }}
              >
                <Typography sx={{ fontWeight: 500, fontSize: "16px", margin: 0 }} paragraph>
                  Job Details
                </Typography>
              </Toolbar>
              <Box sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column", overflow: "hidden" }}>
                <SourcesJobdetailsview jobs={jobs} loading={loading || sourcesLoading} />
              </Box>
            </Paper>
          </Grid>

          {/* --- Contacts --- */}
          <Grid item xs={2.4} sx={{ height: "100%", minHeight: 0 }}>
            <Paper elevation={2} sx={panelPaperStyles}>
              <Toolbar
                sx={{
                  flexShrink: 0,
                  minHeight: "48px !important",
                  bgcolor: "#fff",
                  borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Typography variant="h6" sx={{ fontWeight: 500, fontSize: "16px", margin: 0 }} paragraph>
                  {(selectedJob?.is_direct_contact === 1 && selectedJob?.is_direct_email === 1) || (selectedJob?.is_direct_contact === 1 && selectedJob?.is_direct_email === 0) || (selectedJob?.is_direct_contact === 0 && selectedJob?.is_direct_email === 1) ? 'Meet Hiring Team' : 'Recruiter Details'}
                </Typography>
                {selectedJob?.is_direct_contact === 0 && selectedJob?.is_direct_email === 0 &&
                  <Tooltip
                    title="Recruiter details are shown based on available hiring information from the company."
                    placement="top"
                    arrow
                  >
                    <InfoOutlinedIcon
                      sx={{
                        fontSize: 16,
                        color: "#757575",
                        cursor: "pointer",
                        "&:hover": { color: "#1a73e8" },
                      }}
                    />
                  </Tooltip>
                }
              </Toolbar>
              <Box sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column", overflow: "hidden" }}>
                <Sourcescontacts jobs={jobs} loading={loading || sourcesLoading} />
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </Box>

      <Dialog
        open={filterDialogOpen}
        onClose={() => setFilterDialogOpen(false)}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          style: {
            background: "rgba(0,0,0,0.2)",
            boxShadow: "none",
            backdropFilter: "blur(2px)",
          },
        }}
      >
        <DialogContent
          style={{
            background: "rgba(255,255,255,0.97)",
            boxShadow: "0 2px 16px rgba(0,0,0,0.12)",
            padding: 24,
            borderRadius: 8,
          }}
        >
          <Stack
            direction={"row"}
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography variant="subtitle1" sx={{ mb: 1, mt: 2 }}>
              Company
            </Typography>
            <Button variant="outlined" onClick={handleClearAllFilters}>
              Clear Filters
            </Button>
          </Stack>

          {/* Company search */}
          <TextField
            type="text"
            fullWidth
            variant="outlined"
            label="Search For Companies"
            value={companySearch}
            onChange={handleCompanySearchInputChange}
            sx={{ mb: 2 }}
          />

          {/* Job titles */}
          <Typography variant="subtitle1" sx={{ mb: 1, mt: 2 }}>
            Job Titles
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
            <TextField
              type="text"
              variant="outlined"
              label="Job Title"
              value={jobTitleInput}
              onChange={handleJobTitleInputFieldChange}
              onKeyDown={handleJobTitleKeyDown}
              error={Boolean(jobTitleError)}
              helperText={jobTitleError}
              sx={{ flex: 1 }}
            />
            <IconButton
              color="primary"
              disabled={jobTitleInput.trim() === ""}
              onClick={handleAddJobTitle}
              sx={{
                ml: 1,
                bgcolor:
                  jobTitleInput.trim() === "" ? "#e0e0e0" : "#1976d2",
                color:
                  jobTitleInput.trim() === "" ? "#9e9e9e" : "#fff",
                "&:hover": {
                  bgcolor:
                    jobTitleInput.trim() === "" ? "#e0e0e0" : "#1565c0",
                },
                width: 40,
                height: 40,
                borderRadius: "50%",
              }}
            >
              <TrendingFlatIcon fontSize="small" />
            </IconButton>
          </Box>

          {/* Job title chips */}
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 3 }}>
            {jobTitles.map((jt, idx) => (
              <Chip
                key={idx}
                label={jt}
                onDelete={() => handleRemoveJobTitle(jt)}
                sx={{
                  backgroundColor: "#e0e0e0",
                  cursor: "pointer",
                  "&:hover": { backgroundColor: "#d5d5d5" },
                }}
              />
            ))}
          </Box>

          {/* Job types */}
          <Typography variant="subtitle1" sx={{ mb: 1 }}>
            Job Type
          </Typography>
          <Grid container spacing={1} sx={{ mb: 3 }}>
            {jobTypes.map((jt) => (
              <Grid item xs={6} key={jt.id}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={selectedJobTypes.includes(jt.label)}
                      onChange={() => handleJobTypeToggle(jt.label)}
                      color="primary"
                      size="small"
                    />
                  }
                  label={jt.label}
                  sx={{
                    "& .MuiFormControlLabel-label": {
                      fontSize: 14,
                      marginLeft: "4px",
                    },
                  }}
                />
              </Grid>
            ))}
          </Grid>
        </DialogContent>
        <DialogActions
          style={{
            background: "rgba(255,255,255,0.97)",
            padding: "16px 24px",
            borderBottomLeftRadius: 8,
            borderBottomRightRadius: 8,
            boxShadow: "0 -2px 10px rgba(0,0,0,0.05)"
          }}
        >
          <Button
            variant="outlined"
            color="primary"
            onClick={() => setFilterDialogOpen(false)}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleApplyFilters}
          >
            Submit
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default Sources;
